import { LoginModel } from "../Login/LoginModel";
import apiInstance from "../config/HttpCommon";
import { jwtDecode } from "jwt-decode";
import TokenService from "./authentication/token.service";
import { ENV_CONFIG } from "../config/envConfig";
import { logout } from "../Login/authslice";

export interface DecodedToken {
  exp: number;
  unique_name: string;
  email?: string;
  role?: string;
  userId?: string;
  GlobalAdmin?: string | boolean;
  [key: string]: any;
}

export const addOrUpdateUserSettings = async (data: any) => {
  const response = await apiInstance.post<any>(
    "/Users/Settings",
    data
  );
  return response.data;
  // Ensure a string is always returned
  return "";
};

export const getUserSettings = async (userId: string) => {
  const response = await apiInstance.get<any>(
    "/Users/GetUserSettingById?userId=" + userId
  );
  return response.data;
};

export const getUserAccessById = async (userId: string) => {
  if (!userId || userId.trim() === '') {
    console.warn('getUserAccessById called with empty userId');
    return null;
  }
  
  try {
    const response = await apiInstance.get<any>(
      `/User/Get/${encodeURIComponent(userId)}`
    );
    return response.data;
  } catch (error: any) {
    console.error('Error in getUserAccessById:', {
      userId,
      error: error.message,
      status: error.response?.status
    });
    throw error;
  }
};

export const login = async (login: LoginModel) => {
  // check if user is already logged in
  const accessToken = TokenService.getLocalAccessToken();
  if (accessToken) {
    return "already logged in";
  }

  return await apiInstance.get<any>("/Auth/GetAccessToken?referencetoken=" + login.reference)
    .then((response: any) => {
      const parsedData = response;
      
      if (parsedData.data.accessToken && parsedData.data.refreshToken !== undefined) {
        TokenService.addOrUpdateLocalAccessToken(parsedData.data.accessToken);
        TokenService.addOrUpdateLocalRefreshToken(parsedData.data.refreshToken);
        localStorage.setItem('loginemail', parsedData.data.email || '');
        localStorage.setItem('userId', parsedData.data.userName || parsedData.data.userId || '');
        localStorage.setItem('userRole', parsedData.data.userRole || '');

        return {
          status: "success",
          email: parsedData.data.email,
          userId: parsedData.data.userId,
          userRole: parsedData.data.userRole,
          accessToken: parsedData.data.accessToken,
          refreshToken: parsedData.data.refreshToken
        };
      } else if (parsedData.data.accessToken && !parsedData.data.refreshToken) {
        console.warn("API provided access token but no refresh token. This means token refresh won't be possible.");
        console.warn("Access token will be valid until expiration, then user will need to re-authenticate.");
        
        // Still store the access token
        TokenService.addOrUpdateLocalAccessToken(parsedData.data.accessToken);
        
        // Clear any existing refresh token to avoid confusion
        localStorage.removeItem('refresh_token');
        // Store additional user data
        localStorage.setItem('loginemail', parsedData.data.email || '');
        localStorage.setItem('userId', parsedData.data.userName || parsedData.data.userId || '');
        localStorage.setItem('userRole', parsedData.data.userRole || '');
        
        return {
          status: "success",
          email: parsedData.data.email,
          userId: parsedData.data.userId,
          userRole: parsedData.data.userRole,
          accessToken: parsedData.data.accessToken,
          refreshToken: parsedData.data.refreshToken
        };
      } else {
        const logOutUrl = ENV_CONFIG.logoutUrl;
        localStorage.clear();
        if (logOutUrl)
          window.location.assign(logOutUrl);
      }

    }).catch((error: any) => {
      if (window.location.hostname === "localhost") {
        return "error";
      } else {
        const logOutUrl = ENV_CONFIG.logoutUrl;
        localStorage.clear();
        if (logOutUrl)
          window.location.assign(logOutUrl);
      }
    });
};

export const logoutUser = () => {
  TokenService.clearAllTokens();
  localStorage.clear();
  
  // For user-initiated logout, use the logout URL to properly clear Microsoft session
  const logOutUrl = ENV_CONFIG.logoutUrl;
  if (logOutUrl) {
    window.location.assign(logOutUrl);
  } else {
    window.location.href = window.location.origin + '/logout';
  }
};

export const refershTokenApi = async (): Promise<string> => {
  try {
    const currentToken = TokenService.getLocalAccessToken();
    const refreshToken = TokenService.getLocalRefreshToken();

    if (!currentToken && !refreshToken) {
      logoutUser();
      return "no_tokens";
    }

    const response = await apiInstance.post<any>(
      "Auth/RefreshAccessToken",
      JSON.stringify(refreshToken),
      { headers: { "Content-Type": "application/json" } }
    );

    const parsedData = response.data?.Data;
    if (parsedData?.accessToken) {
      TokenService.addOrUpdateLocalAccessToken(parsedData.accessToken);
      if (parsedData.refreshToken) {
        TokenService.addOrUpdateLocalRefreshToken(parsedData.refreshToken);
      }
      return "success";
    }

    return "invalid_response";
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      logoutUser();
      return "invalid_refresh_token";
    }

    console.error(
      `Users.service: Token refresh failed at ${new Date().toLocaleTimeString()}:`,
      error
    );
    return "error";
  }
};

export const decodeToken = (accessToken: string): DecodedToken | null => {
  if (!accessToken) return null;
  try {
    const decoded: DecodedToken = jwtDecode<DecodedToken>(accessToken);
    return decoded;
  } catch (error) {
    console.error("Invalid Token:", error);
    return null;
  }
};

export const getCurrentUser = async () => {
  try {
    const token = TokenService.getLocalAccessToken();
    if (!token) {
      throw new Error("No access token available");
    }

    const decoded = decodeToken(token);
    if (!decoded) {
      throw new Error("Failed to decode token");
    }

    return {
      userId: decoded.userId || decoded.unique_name,
      email: decoded.email || decoded.unique_name,
      role: decoded.role,
      exp: decoded.exp
    };
  } catch (error) {
    console.error("Failed to get current user:", error);
    throw error;
  }
};

export const isTokenExpired = (token?: string): boolean => {
  try {
    const tokenToCheck = token || TokenService.getLocalAccessToken();
    if (!tokenToCheck) return true;

    const decoded = decodeToken(tokenToCheck);
    if (!decoded) return true;

    return decoded.exp * 1000 <= Date.now();
  } catch (error) {
    return true;
  }
};